<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { getImageSizeUrl } from "@/utils/getImageSizeUrl";
    import RewardsDynamicCustomerIcon from "@apps/rewards/components/RewardsDynamicCustomerIcon.svelte";
    import RewardsGeneralTheme from "@apps/rewards/components/RewardsGeneralTheme.svelte";
    import RewardsList from "@apps/rewards/components/RewardsList/RewardsList.svelte";
    import RewardsSpendingRulesReward from "@apps/rewards/components/RewardsSpendingRules/RewardsSpendingRulesReward.svelte";
    import type { RewardsTranslationNamespaces } from "@apps/rewards/constants/constants";
    import { REWARDS_DATA_GW_SELECTORS } from "@apps/rewards/constants/constants";
    import { REWARDS_SPENDING_RULE_TYPES } from "@apps/rewards/modules/SpendingRules/constants";
    import { REWARDS_SPENDING_RULE_ICONS } from "@apps/rewards/modules/SpendingRules/icons";
    import type RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
    import { REWARDS_ICON_SIZES } from "@apps/rewards/types";
    import type LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
    import type { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
    import { onMount } from "svelte";
    import { fade } from "svelte/transition";

    import T from "@components/T.svelte";
    import Text from "@components/Typography/Text.svelte";

    import RewardPageWidgetAuthButtons from "../../components/RewardPageWidgetAuthButtons.svelte";
    import RewardsPageWidgetCard from "../../components/RewardsPageWidgetCard.svelte";
    import RewardsPageWidgetCardLayoutSkeleton from "../../components/RewardsPageWidgetCardLayoutSkeleton.svelte";
    import RewardsPageWidgetContainer from "../../components/RewardsPageWidgetContainer.svelte";
    import RewardsPageWidgetEmptyState from "../../components/RewardsPageWidgetEmptyState.svelte";
    import RewardsPageWidgetLayout from "../../components/RewardsPageWidgetLayout.svelte";
    import RewardsPageWidgetTitle from "../../components/RewardsPageWidgetTitle.svelte";
    import { rewardsWaysToSpendPageWidgetState } from "./state/rewardsWaysToSpendPageWidgetState";

    //Props
    export let rewardsStorefrontValuesService: RewardsStorefrontValuesService;
    export let urlsBuilder: URLsBuilder;
    export let locationController: LocationControllerInterface;
    export let namespace: RewardsTranslationNamespaces;
    export let afterWidgetInject: () => void;

    //State
    const {
        isCustomer$,
        spendingRulesListViewData$,
        status$,
        pointsName$,
        rewardsCurrencyFormatter,
        rewardsAmountFormatter,
        handleOpenModal,
        checkRequestStatusAndGetSpendingRules,
    } = rewardsWaysToSpendPageWidgetState();

    const rewardsBranding$ =
        rewardsStorefrontValuesService.selectRewardsBranding();

    onMount(() => {
        checkRequestStatusAndGetSpendingRules();

        afterWidgetInject();
    });
</script>

{#if $rewardsBranding$}
    <RewardsGeneralTheme rewardsBrandingGeneral={$rewardsBranding$.general}>
        <div class="gw-rd-ways-to-spend-page-widget">
            <RewardsPageWidgetContainer>
                <RewardsPageWidgetLayout>
                    {#if $status$ === STATUS.INITIAL_LOADING}
                        <RewardsPageWidgetCardLayoutSkeleton />
                    {:else}
                        <RewardsPageWidgetTitle>
                            <T {namespace} translationKey="widget_title" />
                        </RewardsPageWidgetTitle>
                        <div
                            class="gw-rd-ways-to-spend-page-widget__list"
                            in:fade={{ duration: 300 }}
                        >
                            <RewardsList
                                items={$spendingRulesListViewData$}
                                layout="page"
                                let:item
                            >
                                {@const isFreeProduct =
                                    item.type ===
                                    REWARDS_SPENDING_RULE_TYPES.freeProduct}

                                <RewardsPageWidgetCard
                                    isCustomer={$isCustomer$}
                                    isRuleFreeProduct={isFreeProduct}
                                    canOpenModal={item.canRedeem}
                                    dataGwSelector={`${REWARDS_DATA_GW_SELECTORS.rewardsWaysToSpendPageWidget}-${item.id}`}
                                    on:click={() =>
                                        handleOpenModal({
                                            rule: item,
                                            isCustomer: $isCustomer$,
                                        })}
                                >
                                    <svelte:fragment slot="icon">
                                        {#if isFreeProduct}
                                            <RewardsDynamicCustomerIcon
                                                viewBox="0 0 32 32"
                                                customerIcon={item.variants
                                                    .length === 1
                                                    ? item.variants[0].image
                                                    : item.productImage}
                                                defaultIcon={REWARDS_SPENDING_RULE_ICONS[
                                                    item.type
                                                ]}
                                            />
                                        {:else}
                                            <RewardsDynamicCustomerIcon
                                                viewBox="0 0 32 32"
                                                customerIcon={getImageSizeUrl(
                                                    item.icon?.sizes,
                                                    REWARDS_ICON_SIZES.small
                                                ) || item.icon?.url}
                                                defaultIcon={REWARDS_SPENDING_RULE_ICONS[
                                                    item.type
                                                ]}
                                            />
                                        {/if}
                                    </svelte:fragment>
                                    <Text
                                        slot="title"
                                        component="h2"
                                        variant="base"
                                        weight="semibold"
                                    >
                                        {item.title}
                                    </Text>
                                    <RewardsSpendingRulesReward
                                        slot="reward"
                                        variant="base"
                                        rule={item}
                                        isCustomer={$isCustomer$}
                                        pointsName={$pointsName$}
                                        {rewardsCurrencyFormatter}
                                        {rewardsAmountFormatter}
                                        {namespace}
                                    />
                                    <RewardPageWidgetAuthButtons
                                        slot="auth-buttons"
                                        {locationController}
                                        {namespace}
                                        {urlsBuilder}
                                    />
                                </RewardsPageWidgetCard>
                                <RewardsPageWidgetEmptyState slot="empty-state">
                                    <Text slot="content">
                                        <T
                                            {namespace}
                                            translationKey="spending_rules_empty_state"
                                        />
                                    </Text>
                                </RewardsPageWidgetEmptyState>
                            </RewardsList>
                        </div>
                    {/if}
                </RewardsPageWidgetLayout>
            </RewardsPageWidgetContainer>
        </div>
    </RewardsGeneralTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../../../themes/globals.css";

    .gw-rd-ways-to-spend-page-widget {
        --gw-image-skeleton-backdrop-color: #e2e8f0;
        background-color: var(--gw-white-bg);

        &__list {
            width: 100%;

            &--empty {
                display: none;
            }
        }
    }
</style>
